// v1.33: /ocr/history — OCR 任务历史 (最近 50 条)
// 移动端 card 列表, 桌面表格
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { EmptyState } from '../components/EmptyState';
import { Icon } from '../components/Icon';
import { Skeleton } from '../components/Skeleton';
import { Alert } from '../components/ui/Alert';
import { Card } from '../components/ui/Card';
import { type OcrJob, ocrApi } from '../lib/api';

export const Route = createFileRoute('/ocr/history')({
  component: OcrHistoryPage,
});

const R2_PUBLIC_BASE = 'https://trade-os-files.maclog.workers.dev';

const STATUS_LABEL: Record<OcrJob['status'], string> = {
  pending: '处理中',
  extracted: '已识别',
  imported: '已导入',
  failed: '失败',
};

const STATUS_CLASS: Record<OcrJob['status'], string> = {
  pending: 'bg-accent-soft text-accent',
  extracted: 'bg-good/10 text-good',
  imported: 'bg-good/20 text-good',
  failed: 'bg-bad/10 text-bad',
};

function OcrHistoryPage() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['ocr-jobs'],
    queryFn: () => ocrApi.listJobs(),
    staleTime: 30_000,
  });

  return (
    <div className="animate-fade-up">
      <div className="mb-4 flex items-center gap-2 text-sm">
        <Link
          to="/ocr"
          className="inline-flex items-center gap-1 text-ink-3 hover:text-accent min-h-[44px] px-2 -ml-2"
        >
          <Icon name="arrow-left" size="sm" aria-hidden />
          返回 OCR 上传
        </Link>
      </div>

      <header className="mb-5 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-ink leading-tight flex items-center gap-2">
            <Icon name="clock" size="md" className="text-accent" aria-hidden />
            OCR 任务历史
          </h1>
          <p className="text-xs sm:text-sm text-ink-3 mt-1 leading-relaxed">
            最近 50 次名片识别 — 可找回历史识别结果, 失败重试, 重复导入。
          </p>
        </div>
        <button
          type="button"
          onClick={() => queryClient.invalidateQueries({ queryKey: ['ocr-jobs'] })}
          className="text-xs text-ink-3 hover:text-accent inline-flex items-center gap-1 min-h-[36px] px-3"
        >
          <Icon name="activity" size="xs" aria-hidden />
          刷新
        </button>
      </header>

      {isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      )}

      {isError && (
        <Alert variant="error">
          加载失败: {(error as Error).message}{' '}
          <button
            type="button"
            onClick={() => refetch()}
            className="ml-2 underline hover:no-underline"
          >
            重试
          </button>
        </Alert>
      )}

      {!isLoading && !isError && data?.items.length === 0 && (
        <EmptyState
          icon="sparkles"
          title="还没有 OCR 任务"
          description="去上传一张名片, AI 帮你识别 13 字段"
          action={
            <Link
              to="/ocr"
              className="px-3 py-2 bg-accent text-white rounded text-sm hover:bg-accent-2 inline-flex items-center gap-1"
            >
              <Icon name="upload" size="sm" aria-hidden />
              立即上传
            </Link>
          }
        />
      )}

      {data?.items && data.items.length > 0 && (
        <>
          <p className="text-xs text-ink-3 mb-3">
            共 <b>{data.total}</b> 条任务
          </p>

          {/* 桌面: 表格 */}
          <Card padding="none" className="hidden md:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line bg-surface-2 text-ink-3 text-xs uppercase">
                  <th className="text-left px-3 py-2 font-medium">缩略图</th>
                  <th className="text-left px-3 py-2 font-medium">公司 / 联系人</th>
                  <th className="text-left px-3 py-2 font-medium">联系方式</th>
                  <th className="text-left px-3 py-2 font-medium">状态</th>
                  <th className="text-left px-3 py-2 font-medium">时间</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((job) => (
                  <JobRow key={job.id} job={job} />
                ))}
              </tbody>
            </table>
          </Card>

          {/* 移动: card 列表 */}
          <div className="md:hidden space-y-2">
            {data.items.map((job) => (
              <JobCard key={job.id} job={job} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function JobRow({ job }: { job: OcrJob }) {
  const f = job.extractedJson;
  const imgUrl = `${R2_PUBLIC_BASE}/${job.imageR2Key}`;
  return (
    <tr className="border-b border-line last:border-b-0 hover:bg-surface-2/40">
      <td className="px-3 py-2">
        <img
          src={imgUrl}
          alt="名片缩略图"
          className="w-12 h-12 object-cover rounded border border-line"
          loading="lazy"
          referrerPolicy="no-referrer"
        />
      </td>
      <td className="px-3 py-2">
        <div className="font-medium text-ink-2">{f?.company ?? '—'}</div>
        <div className="text-xs text-ink-3">
          {[f?.contactName, f?.title].filter(Boolean).join(' · ') || '—'}
        </div>
      </td>
      <td className="px-3 py-2 text-xs text-ink-3">
        <div>{f?.email ?? '—'}</div>
        <div>{f?.phone ?? '—'}</div>
      </td>
      <td className="px-3 py-2">
        <span
          className={`inline-block px-2 py-0.5 text-[10px] rounded ${STATUS_CLASS[job.status]}`}
        >
          {STATUS_LABEL[job.status]}
        </span>
        {job.customerId && (
          <Link
            to="/customers/$customerId"
            params={{ customerId: job.customerId }}
            className="ml-2 text-xs text-accent hover:underline"
          >
            客户 →
          </Link>
        )}
      </td>
      <td className="px-3 py-2 text-xs text-ink-3 whitespace-nowrap">
        {new Date(job.createdAt).toLocaleString('zh-CN')}
      </td>
    </tr>
  );
}

function JobCard({ job }: { job: OcrJob }) {
  const f = job.extractedJson;
  const imgUrl = `${R2_PUBLIC_BASE}/${job.imageR2Key}`;
  return (
    <Card padding="sm">
      <div className="flex gap-3">
        <img
          src={imgUrl}
          alt="名片缩略图"
          className="w-16 h-16 object-cover rounded border border-line shrink-0"
          loading="lazy"
          referrerPolicy="no-referrer"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="font-medium text-sm text-ink-2 truncate">
              {f?.company ?? '(未识别公司名)'}
            </div>
            <span
              className={`shrink-0 inline-block px-2 py-0.5 text-[10px] rounded ${STATUS_CLASS[job.status]}`}
            >
              {STATUS_LABEL[job.status]}
            </span>
          </div>
          {f?.contactName && (
            <div className="text-xs text-ink-3 mt-0.5">
              {f.contactName}
              {f.title ? ` · ${f.title}` : ''}
            </div>
          )}
          <div className="text-[10px] text-ink-3 mt-0.5 space-x-2">
            {f?.email && <span>{f.email}</span>}
            {f?.phone && <span>{f.phone}</span>}
          </div>
          <div className="flex items-center justify-between mt-1.5">
            <span className="text-[10px] text-ink-3">
              {new Date(job.createdAt).toLocaleString('zh-CN')}
            </span>
            {job.customerId && (
              <Link
                to="/customers/$customerId"
                params={{ customerId: job.customerId }}
                className="text-[10px] text-accent hover:underline"
              >
                客户详情 →
              </Link>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
